<template>
  <ContentWrap>
    <el-alert
      type="info"
      :closable="false"
      show-icon
      title="可登记挂靠（待平台审核）、启停接单与调整日限。机构端不可自行「通过/驳回」审核；改挂请联系平台。"
      class="mb-12px"
    />
    <el-form :inline="true" :model="query" class="mb-12px" @submit.prevent>
      <el-form-item label="姓名">
        <el-input v-model="query.name" clearable class="!w-160px" />
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="query.verifyStatus" clearable placeholder="全部" class="!w-140px">
          <el-option label="待审" value="PENDING" />
          <el-option label="已通过" value="APPROVED" />
          <el-option label="已驳回" value="REJECTED" />
          <el-option label="已禁用" value="DISABLED" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleQuery">搜索</el-button>
        <el-button @click="resetQuery">重置</el-button>
        <el-button type="success" @click="openRegister">登记挂靠</el-button>
      </el-form-item>
    </el-form>
    <el-table v-loading="loading" :data="list" border>
      <el-table-column prop="id" label="编号" width="80" />
      <el-table-column prop="name" label="姓名" min-width="100" />
      <el-table-column prop="phone" label="手机" width="120" />
      <el-table-column prop="certNo" label="执业证号" min-width="140" show-overflow-tooltip />
      <el-table-column prop="city" label="城市" width="90" />
      <el-table-column label="准入" width="110">
        <template #default="{ row }">
          <el-tag :type="verifyTagType(row.verifyStatus)" size="small">
            {{ verifyLabel(row.verifyStatus) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="日上限" width="90">
        <template #default="{ row }">
          {{ row.dailyLimit ?? '—' }}
          <span v-if="row.todayGrabCount != null" class="text-gray-400 text-12px">
            /今日{{ row.todayGrabCount }}
          </span>
        </template>
      </el-table-column>
      <el-table-column label="接单" width="90">
        <template #default="{ row }">
          <el-switch
            :model-value="!!row.enabled"
            :disabled="row.verifyStatus !== 'APPROVED' || switchingId === row.id"
            @change="(val) => onToggleEnabled(row, !!val)"
          />
        </template>
      </el-table-column>
      <el-table-column label="操作" width="100" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" @click="openDailyLimit(row)">日限</el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="query.pageNo"
      v-model:limit="query.pageSize"
      :total="total"
      @pagination="getList"
    />

    <Dialog v-model="limitVisible" title="调整日接单上限" width="400px">
      <el-form label-width="100px">
        <el-form-item label="律师">
          <span>{{ limitRow?.name || '#' + limitRow?.id }}</span>
        </el-form-item>
        <el-form-item label="日上限" required>
          <el-input-number v-model="limitValue" :min="1" :max="50" />
          <span class="ml-8px text-gray-400 text-12px">1～50</span>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="limitVisible = false">取消</el-button>
        <el-button type="primary" :loading="limitSaving" @click="submitDailyLimit">保存</el-button>
      </template>
    </Dialog>

    <Dialog v-model="registerVisible" title="登记挂靠律师（待审）" width="480px">
      <el-alert
        type="warning"
        :closable="false"
        show-icon
        title="提交后为「待审」且不可接单，须平台审核通过后方可启用。机构端不能自行通过。"
        class="mb-12px"
      />
      <el-form ref="registerFormRef" :model="registerForm" :rules="registerRules" label-width="100px">
        <el-form-item label="姓名" prop="name">
          <el-input v-model="registerForm.name" maxlength="64" />
        </el-form-item>
        <el-form-item label="手机号" prop="mobile">
          <el-input v-model="registerForm.mobile" maxlength="11" placeholder="作为律师登录账号" />
        </el-form-item>
        <el-form-item label="执业证号" prop="certNo">
          <el-input v-model="registerForm.certNo" maxlength="64" />
        </el-form-item>
        <el-form-item label="初始密码" prop="password">
          <el-input
            v-model="registerForm.password"
            type="password"
            show-password
            maxlength="16"
            placeholder="可选；仅新建会员时生效"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="registerVisible = false">取消</el-button>
        <el-button type="primary" :loading="registerSaving" @click="submitRegister">提交待审</el-button>
      </template>
    </Dialog>
  </ContentWrap>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import { useRoute } from 'vue-router'
import type { FormInstance, FormRules } from 'element-plus'
import { FirmPortalApi, type FirmLawyerVO } from '@/api/firm/portal'

defineOptions({ name: 'FirmPortalLawyer' })

const message = useMessage()
const route = useRoute()
const loading = ref(false)
const list = ref<FirmLawyerVO[]>([])
const total = ref(0)
const switchingId = ref<number>()
const query = reactive({
  pageNo: 1,
  pageSize: 10,
  name: undefined as string | undefined,
  verifyStatus: undefined as string | undefined
})

const limitVisible = ref(false)
const limitSaving = ref(false)
const limitRow = ref<FirmLawyerVO | null>(null)
const limitValue = ref(5)

const registerVisible = ref(false)
const registerSaving = ref(false)
const registerFormRef = ref<FormInstance>()
const registerForm = reactive({
  name: '',
  mobile: '',
  certNo: '',
  password: ''
})
const registerRules: FormRules = {
  name: [{ required: true, message: '请输入姓名', trigger: 'blur' }],
  mobile: [
    { required: true, message: '请输入手机号', trigger: 'blur' },
    { pattern: /^1\d{10}$/, message: '手机号格式不正确', trigger: 'blur' }
  ],
  certNo: [{ required: true, message: '请输入执业证号', trigger: 'blur' }],
  password: [
    {
      validator: (_r, v, cb) => {
        if (!v) return cb()
        if (String(v).length < 4 || String(v).length > 16) {
          return cb(new Error('密码 4～16 位'))
        }
        cb()
      },
      trigger: 'blur'
    }
  ]
}

const verifyLabel = (status?: string) => {
  const map: Record<string, string> = {
    PENDING: '待审',
    APPROVED: '已通过',
    REJECTED: '已驳回',
    DISABLED: '已禁用'
  }
  return (status && map[status]) || status || '—'
}

const verifyTagType = (status?: string) => {
  if (status === 'APPROVED') return 'success'
  if (status === 'PENDING') return 'warning'
  if (status === 'REJECTED' || status === 'DISABLED') return 'danger'
  return 'info'
}

const getList = async () => {
  loading.value = true
  try {
    const data = await FirmPortalApi.lawyerPage(query)
    list.value = data?.list || []
    total.value = data?.total || 0
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  query.pageNo = 1
  getList()
}

const resetQuery = () => {
  query.name = undefined
  query.verifyStatus = undefined
  handleQuery()
}

const onToggleEnabled = async (row: FirmLawyerVO, enabled: boolean) => {
  if (row.verifyStatus !== 'APPROVED') {
    message.warning('待平台审核通过后方可启用')
    return
  }
  switchingId.value = row.id
  try {
    await FirmPortalApi.updateLawyerEnabled({ id: row.id, enabled })
    message.success(enabled ? '已启用接单' : '已停用接单')
    row.enabled = enabled
  } catch {
    await getList()
  } finally {
    switchingId.value = undefined
  }
}

const openDailyLimit = (row: FirmLawyerVO) => {
  limitRow.value = row
  limitValue.value = row.dailyLimit && row.dailyLimit >= 1 ? row.dailyLimit : 5
  limitVisible.value = true
}

const submitDailyLimit = async () => {
  if (!limitRow.value?.id) return
  if (limitValue.value < 1 || limitValue.value > 50) {
    message.warning('日上限须为 1～50')
    return
  }
  limitSaving.value = true
  try {
    await FirmPortalApi.updateLawyerDailyLimit({
      id: limitRow.value.id,
      dailyLimit: limitValue.value
    })
    message.success('日上限已更新')
    limitVisible.value = false
    await getList()
  } finally {
    limitSaving.value = false
  }
}

const openRegister = () => {
  registerForm.name = ''
  registerForm.mobile = ''
  registerForm.certNo = ''
  registerForm.password = ''
  registerVisible.value = true
}

const submitRegister = async () => {
  await registerFormRef.value?.validate()
  registerSaving.value = true
  try {
    const id = await FirmPortalApi.registerLawyerPending({
      name: registerForm.name.trim(),
      mobile: registerForm.mobile.trim(),
      certNo: registerForm.certNo.trim(),
      password: registerForm.password?.trim() || undefined
    })
    message.success(`已提交挂靠待审（#${id}），请等待平台审核`)
    registerVisible.value = false
    query.verifyStatus = 'PENDING'
    query.pageNo = 1
    await getList()
  } finally {
    registerSaving.value = false
  }
}

onMounted(() => {
  if (typeof route.query.verifyStatus === 'string' && route.query.verifyStatus) {
    query.verifyStatus = route.query.verifyStatus
  }
  getList()
})
</script>
